import { Cylinder, Package, Mountain } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const CARGO = [
  {
    id: "steel-coils",
    icon: Cylinder,
    name: "Steel Coils",
    tag: "Metals",
    body: "Eye-to-side or eye-to-sky loading with coil cradles, chain lashing and edge protectors as standard — mill, port and plant movements.",
    specs: [
      ["Typical load", "20–28 MT per trailer"],
      ["Vehicles", "Flatbed · Open body 19–32 FT"],
      ["Securing", "Coil cradles · Chains · Edge protectors"],
    ],
  },
  {
    id: "cement",
    icon: Package,
    name: "Cement",
    tag: "Building Materials",
    body: "Bagged and bulk cement moved in tarpaulin-covered bodies with moisture checks at loading — plant-to-depot and project-site distribution.",
    specs: [
      ["Formats", "50 kg bags · Bulk tanker"],
      ["Vehicles", "Covered open body · Container 20/40 FT"],
      ["Care", "Tarpaulin cover · Moisture check at loading"],
    ],
  },
  {
    id: "coal",
    icon: Mountain,
    name: "Coal",
    tag: "Bulk Commodities",
    body: "High-volume haulage from mines and ports to power plants and industry, with weighbridge-documented dispatch on every load.",
    specs: [
      ["Routes", "Mine / port → plant"],
      ["Vehicles", "High-sided open body · Tipper"],
      ["Control", "Weighbridge-documented loads"],
    ],
  },
];

export const Commodities = () => (
  <section
    id="cargo"
    data-testid="cargo-section"
    className="scroll-mt-20 py-24 sm:py-32 border-t border-slate-800"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHead
        overline="What We Haul"
        title="Cargo profiles."
        sub="Every commodity has its own handling discipline. These are the loads our lanes are built around — secured, documented and delivered to spec."
      />

      <div className="mt-14 grid md:grid-cols-3 gap-px bg-slate-800 border border-slate-800">
        {CARGO.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.1}>
            <article
              data-testid={`cargo-card-${c.id}`}
              className="group relative bg-[#161B22] hover:bg-[#1A212B] transition-colors duration-300 h-full p-7 border-t-2 border-transparent hover:border-amber-500"
            >
              <div className="flex items-center justify-between">
                <span className="w-11 h-11 flex items-center justify-center border border-slate-700 group-hover:border-amber-500 transition-colors duration-300">
                  <c.icon className="w-5 h-5 text-amber-500" />
                </span>
                <span className="mono text-[10px] uppercase tracking-[0.2em] text-slate-600 group-hover:text-amber-500/80 transition-colors duration-300">
                  {c.tag}
                </span>
              </div>
              <h3 className="font-display mt-6 text-xl sm:text-2xl font-bold uppercase tracking-wide text-slate-100">
                {c.name}
              </h3>
              <p className="mt-3 text-base text-slate-400 leading-relaxed">{c.body}</p>
              <dl className="mt-6 pt-5 border-t border-slate-800 flex flex-col gap-3">
                {c.specs.map(([k, v]) => (
                  <div key={k} className="flex flex-col gap-1">
                    <dt className="mono text-[10px] uppercase tracking-[0.2em] text-slate-600">{k}</dt>
                    <dd className="mono text-xs uppercase tracking-[0.1em] text-amber-500/90">{v}</dd>
                  </div>
                ))}
              </dl>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <p className="mono mt-8 text-[11px] uppercase tracking-[0.15em] text-slate-500">
          Also moving: 20 / 40 FT containers · ODC machinery · project cargo — ask the desk about your load
        </p>
      </Reveal>
    </div>
  </section>
);
